import { Ellipsis, Pencil, Trash2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import type { Highlight } from '@/api/services/highlight'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { formatTime } from '@/lib/cue-parser'

type Props = {
  isOpen: boolean
  onClose: () => void
  bookmarks: Highlight[]
  onSeek: (seconds: number) => void
  onUpdateNote: (bookmarkId: number, note: string) => void
  onDelete: (bookmarkId: number) => void
}

export const BookmarkSheet = ({ isOpen, onClose, bookmarks, onSeek, onUpdateNote, onDelete }: Props) => {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editNote, setEditNote] = useState('')
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const sorted = [...bookmarks].sort((a, b) => (a.position_data.timestamp ?? 0) - (b.position_data.timestamp ?? 0))

  const handleStartEdit = useCallback((bookmark: Highlight) => {
    setEditingId(bookmark.id)
    setEditNote(bookmark.note ?? '')
    // フォーカスは次のレンダリング後に設定
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const handleSaveNote = useCallback(() => {
    if (editingId === null) return
    onUpdateNote(editingId, editNote)
    setEditingId(null)
    setEditNote('')
  }, [editingId, editNote, onUpdateNote])

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
    setEditNote('')
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (deleteTargetId === null) return
    onDelete(deleteTargetId)
    setDeleteTargetId(null)
  }, [deleteTargetId, onDelete])

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="max-h-[70vh]">
          <SheetHeader>
            <SheetTitle>ブックマーク</SheetTitle>
          </SheetHeader>

          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">ブックマークはまだありません</p>
          ) : (
            <div className="overflow-y-auto mt-4 -mx-2">
              {sorted.map((bm) => {
                const ts = bm.position_data.timestamp ?? 0
                const isEditing = editingId === bm.id

                return (
                  <div key={bm.id} className="flex items-center gap-2 px-4 py-3 hover:bg-muted rounded-md">
                    {/* メイン領域：タップでシーク */}
                    <button type="button" onClick={() => onSeek(ts)} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground shrink-0 w-14 text-right font-mono">
                          {formatTime(ts)}
                        </span>
                        <div className="flex-1 min-w-0">
                          {bm.text && <p className="text-sm truncate">{bm.text}</p>}
                          {isEditing ? (
                            <div className="flex items-center gap-2 mt-1" onClick={(e) => e.stopPropagation()}>
                              <Input
                                ref={inputRef}
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveNote()
                                  if (e.key === 'Escape') handleCancelEdit()
                                }}
                                placeholder="メモを入力..."
                                className="h-7 text-xs"
                              />
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleSaveNote}>
                                保存
                              </Button>
                            </div>
                          ) : (
                            bm.note && <p className="text-xs text-muted-foreground truncate mt-0.5">{bm.note}</p>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* 3点メニュー */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1.5 rounded-md hover:bg-muted-foreground/10 shrink-0">
                          <Ellipsis size={16} className="text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleStartEdit(bm)}>
                          <Pencil size={14} className="mr-2" />
                          メモ編集
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteTargetId(bm.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 size={14} className="mr-2" />
                          削除
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )
              })}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* 削除確認ダイアログ */}
      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => !open && setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ブックマークを削除</AlertDialogTitle>
            <AlertDialogDescription>このブックマークを削除しますか？</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
